import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Merge, X, Sparkles, Search } from "lucide-react";
import { toast } from "../../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdr = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token") || ""}` } });

// Merge dialog: pick the master card + final name, every record of the others is rewritten to the master phone/email.
export function MergeCustomersDialog({ customers, onClose, onDone }) {
  const fid = customers.find((c) => (c.sources || []).includes("fidelity"));
  const best = fid || [...customers].sort((a, b) => (b.visits || 0) - (a.visits || 0))[0];
  const [master, setMaster] = useState(best?.key);
  const [name, setName] = useState(best?.name || "");
  const [busy, setBusy] = useState(false);
  const m = customers.find((c) => c.key === master);
  const names = [...new Set(customers.map((c) => c.name).filter(Boolean))];

  const merge = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/customers/merge`, { master_key: master, alias_keys: customers.filter((c) => c.key !== master).map((c) => c.key), name: name.trim() || undefined }, hdr());
      toast({ title: "Schede unite", description: `${data.merged} schede fuse in "${data.name}" · ${data.updated_records} record aggiornati` });
      onDone();
    } catch (e) { toast({ title: "Unione non riuscita", description: e?.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="merge-dialog">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#4a1c17] text-[#f7ebdf] px-4 py-3 flex items-center justify-between sticky top-0">
          <h3 className="font-serif text-lg font-bold inline-flex items-center gap-2"><Merge className="h-4 w-4" /> Unisci {customers.length} schede</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-white/10" data-testid="merge-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 space-y-4 text-sm">
          <div>
            <div className="text-xs font-semibold text-[#7c5a4e] mb-1.5">Scheda principale (telefono/email che restano)</div>
            <div className="space-y-1.5">
              {customers.map((c) => {
                const isFid = (c.sources || []).includes("fidelity");
                return (
                  <label key={c.key} className={`flex items-center gap-3 rounded-xl border px-3 py-2 cursor-pointer ${master === c.key ? "border-[#4a1c17] bg-[#faf3ee]" : "border-[#e8dcc8]"} ${fid && !isFid ? "opacity-70" : ""}`} data-testid={`merge-master-${c.key}`}>
                    <input type="radio" name="master" checked={master === c.key} disabled={!!fid && !isFid} onChange={() => { setMaster(c.key); setName(c.name || name); }} className="accent-[#4a1c17]" />
                    <span className="flex-1 min-w-0">
                      <span className="font-semibold text-[#2b1410] block truncate">{c.name || "(senza nome)"} {isFid && <span className="ml-1 rounded-full bg-[#4a1c17] text-white px-1.5 py-0.5 text-[10px] inline-flex items-center gap-0.5"><Sparkles className="h-2.5 w-2.5" /> Fidelity</span>}</span>
                      <span className="text-[11px] text-[#7c5a4e] block truncate">{c.phone || "—"} {c.email ? `· ${c.email}` : ""} · {c.visits || 0} visite · {(c.sources || []).join(", ")}</span>
                    </span>
                  </label>
                );
              })}
            </div>
            {fid && <p className="text-[11px] text-[#7c5a4e] mt-1">L'account Fidelity è sempre la scheda principale.</p>}
          </div>
          <div>
            <div className="text-xs font-semibold text-[#7c5a4e] mb-1.5">Nome finale</div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {names.map((n) => <button key={n} type="button" onClick={() => setName(n)} className={`rounded-full px-3 py-1 text-xs font-semibold border ${name === n ? "bg-[#4a1c17] text-white border-[#4a1c17]" : "border-[#e8dcc8] text-[#4a1c17]"}`} data-testid="merge-name-option">{n}</button>)}
            </div>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2 text-sm" data-testid="merge-name" />
          </div>
          {m && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900" data-testid="merge-preview">
              Tutte le prenotazioni, soggiorni e schede delle altre {customers.length - 1} voci verranno aggiornate con <b>{name || m.name}</b>{m.phone ? <> · <b>{m.phone}</b></> : ""}{m.email ? <> · <b>{m.email}</b></> : ""}. Lo storico (visite, spesa, WhatsApp) si somma in un'unica scheda. L'operazione non è reversibile.
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="rounded-full border border-[#e8dcc8] px-4 py-1.5 text-sm">Annulla</button>
            <button onClick={merge} disabled={busy || !master} className="rounded-full bg-[#4a1c17] text-white px-4 py-1.5 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="merge-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Merge className="h-4 w-4" />} Unisci</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Lists probable duplicates (same name, different phone/email) with a one-tap merge.
export function DuplicatesDialog({ onClose, onMerge }) {
  const [groups, setGroups] = useState(null);
  useEffect(() => { axios.get(`${API}/admin/customers/duplicates`, hdr()).then((r) => setGroups(r.data.groups)).catch(() => setGroups([])); }, []);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="duplicates-dialog">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#4a1c17] text-[#f7ebdf] px-4 py-3 flex items-center justify-between">
          <h3 className="font-serif text-lg font-bold inline-flex items-center gap-2"><Search className="h-4 w-4" /> Possibili doppioni {groups && <span className="rounded-full bg-white/15 px-2 py-0.5 text-xs">{groups.length}</span>}</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-white/10" data-testid="duplicates-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 overflow-y-auto space-y-2 text-sm">
          {!groups && <div className="flex items-center gap-2 text-[#7c5a4e]"><Loader2 className="h-4 w-4 animate-spin" /> Analisi archivio…</div>}
          {groups && groups.length === 0 && <div className="text-[#7c5a4e] py-6 text-center">Nessun doppione evidente: stesso nome con contatti diversi non trovato.</div>}
          {groups?.map((g, i) => (
            <div key={i} className="rounded-xl border border-[#e8dcc8] p-3 flex items-start gap-3" data-testid={`dup-group-${i}`}>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[#2b1410]">{g.name}</div>
                <ul className="mt-1 space-y-0.5 text-[11px] text-[#7c5a4e]">
                  {g.items.map((c) => <li key={c.key} className="truncate">{c.phone || c.email || "senza contatto"} · {c.visits} visite · {(c.sources || []).join(", ")} {c.last_activity && `· ultima ${c.last_activity.split("-").reverse().join("/")}`}</li>)}
                </ul>
              </div>
              <button onClick={() => onMerge(g.items)} className="rounded-full bg-[#4a1c17] text-white px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 shrink-0" data-testid={`dup-merge-${i}`}><Merge className="h-3.5 w-3.5" /> Unisci</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
